import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

type ShortcutHandler = () => void;

type Shortcut = {
  key: string;
  meta?: boolean; // Cmd on Mac, Ctrl on Windows
  shift?: boolean;
  description: string;
  handler: ShortcutHandler;
};

export function useKeyboardShortcuts(shortcuts: Shortcut[]) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      for (const shortcut of shortcuts) {
        const metaMatch = shortcut.meta ? event.metaKey || event.ctrlKey : true;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();

        if (metaMatch && shiftMatch && keyMatch) {
          event.preventDefault();
          shortcut.handler();
          return;
        }
      }
    },
    [shortcuts]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

// Global navigation shortcuts
export function useGlobalShortcuts() {
  const navigate = useNavigate();

  useKeyboardShortcuts([
    {
      key: 'n',
      meta: true,
      description: 'Go to Research (new niche)',
      handler: () => navigate('/research'),
    },
    {
      key: 'b',
      meta: true,
      description: 'Go to Batch Generate',
      handler: () => navigate('/batch'),
    },
    {
      key: 'i',
      meta: true,
      description: 'Go to Inventory',
      handler: () => navigate('/inventory'),
    },
    {
      key: 'h',
      meta: true,
      description: 'Go to Dashboard (home)',
      handler: () => navigate('/'),
    },
    {
      key: ',',
      meta: true,
      description: 'Go to Settings',
      handler: () => navigate('/settings'),
    },
    {
      key: '/',
      meta: true,
      description: 'Go to Help',
      handler: () => navigate('/help'),
    },
  ]);
}

// Keyboard shortcuts help modal data
export const KEYBOARD_SHORTCUTS = [
  { keys: ['Cmd/Ctrl', 'H'], description: 'Go to Dashboard' },
  { keys: ['Cmd/Ctrl', 'N'], description: 'Research Niches' },
  { keys: ['Cmd/Ctrl', 'B'], description: 'Batch Generate' },
  { keys: ['Cmd/Ctrl', 'I'], description: 'Inventory' },
  { keys: ['Cmd/Ctrl', ','], description: 'Settings' },
  { keys: ['Cmd/Ctrl', '/'], description: 'Help' },
];
